import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { useIdentity } from '../identityContext';

// Step 2 page for granting an admin login access. The approver email links
// here (/confirm-admin-credentials/<token>) — IdentityProvider gates it on a
// valid admin session. The token only identifies the request; the resolver
// re-checks superAdmin + recipient + not-initiator.
export function ConfirmAdminCredentialsPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const identity = useIdentity();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, loading } = useQuery(query, {
    variables: { token: token ?? '' },
    skip: token == null,
    fetchPolicy: 'cache-and-network'
  });

  const [confirm, { loading: confirming }] = useMutation(confirmMutation);
  const [revokeConfirm, { loading: revokeConfirming }] = useMutation(revokeConfirmMutation);
  const [reject, { loading: rejecting }] = useMutation(rejectMutation);

  if (token == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm administrator access" onBack={() => navigate('/')} />
        <Alert severity="error">Missing confirmation token.</Alert>
      </MainWrapper>
    );
  }

  if (loading && data == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm administrator access" onBack={() => navigate('/')} />
        <Typography variant="body2">Loading…</Typography>
      </MainWrapper>
    );
  }

  const action = data?.pendingAdminActionByToken ?? null;

  if (action == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm administrator access" onBack={() => navigate('/')} />
        <Alert severity="error">This confirmation link is invalid or has been deleted.</Alert>
      </MainWrapper>
    );
  }

  if (action.status === 'confirmed') {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm administrator access" onBack={() => navigate('/')} />
        <Alert severity="info">
          This request was already confirmed
          {action.confirmedByEmail != null ? ` by ${action.confirmedByEmail}` : ''}. No further
          action is needed.
        </Alert>
      </MainWrapper>
    );
  }

  if (action.status === 'cancelled' || action.status === 'rejected') {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm administrator access" onBack={() => navigate('/')} />
        <Alert severity="info">This request was {action.status} and is no longer actionable.</Alert>
      </MainWrapper>
    );
  }

  if (action.status === 'expired') {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Confirm administrator access" onBack={() => navigate('/')} />
        <Alert severity="warning">
          This confirmation link has expired (expired {new Date(action.expiresAt).toLocaleString()}
          ). Ask <strong>{action.initiatedByEmail}</strong> to re-send the request.
        </Alert>
      </MainWrapper>
    );
  }

  const isRevoke = action.kind === 'adminCredentialsRevoke';
  const busy = confirming || revokeConfirming || rejecting;

  return (
    <MainWrapper>
      <MainWrapperHeader
        title={isRevoke ? 'Confirm administrator access revoke' : 'Confirm administrator access'}
        description={
          isRevoke
            ? 'Removes this administrator’s access to the admin app.'
            : 'Grants login credentials to a draft administrator.'
        }
        onBack={() => navigate('/')}
      />

      <Stack spacing={4} sx={{ maxWidth: 720 }}>
        <Box sx={{ p: 3, borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography
            variant="labelSmall"
            color="text.secondary"
            sx={{ mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {isRevoke ? 'You are about to revoke access from' : 'You are about to grant access to'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1 }}>
            <Typography variant="labelSmall" color="text.secondary">
              Administrator
            </Typography>
            <Typography variant="body2">
              {action.adminName != null && action.adminName !== '' && (
                <strong>{action.adminName} </strong>
              )}
              {action.adminEmail}
            </Typography>

            <Typography variant="labelSmall" color="text.secondary">
              Role
            </Typography>
            <Typography variant="body2">
              {action.adminRole === 'superAdmin' ? 'Superadmin' : 'Admin'}
            </Typography>

            <Typography variant="labelSmall" color="text.secondary">
              Requested by
            </Typography>
            <Typography variant="body2">{action.initiatedByEmail}</Typography>

            <Typography variant="labelSmall" color="text.secondary">
              Expires
            </Typography>
            <Typography variant="body2">{new Date(action.expiresAt).toLocaleString()}</Typography>
          </Box>
        </Box>

        {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}

        {!identity.isActionConfirmationRecipient && (
          <Alert severity="info">
            Only administrators listed in <code>ADMIN_ACTION_CONFIRMATION_EMAILS</code> can confirm
            an access request. The buttons are hidden because your account isn't a recipient.
          </Alert>
        )}

        <Stack direction="row" spacing={3}>
          {identity.isActionConfirmationRecipient && (
            <>
              <Button
                variant="primary"
                disabled={busy}
                onClick={async () => {
                  setErrorMessage(null);
                  try {
                    if (isRevoke) {
                      const result = await revokeConfirm({ variables: { token } });
                      const adminId = result.data?.adminCredentialsRevokeConfirm.id;
                      if (adminId != null) navigate(`/administrators/${adminId}/credentials`);
                    } else {
                      const result = await confirm({ variables: { token } });
                      const adminId = result.data?.adminCredentialsConfirm.id;
                      if (adminId != null) navigate(`/administrators/${adminId}/credentials`);
                    }
                  } catch (err: any) {
                    setErrorMessage(err.message ?? 'Failed to confirm');
                  }
                }}
              >
                {confirming || revokeConfirming
                  ? 'Confirming…'
                  : isRevoke
                    ? 'Confirm revoke'
                    : 'Confirm access'}
              </Button>
              <Button
                variant="destructive"
                disabled={busy}
                onClick={async () => {
                  setErrorMessage(null);
                  const reason = window.prompt('Reason for rejecting (optional):') ?? undefined;
                  try {
                    await reject({ variables: { id: action.id, reason } });
                    navigate('/pending-actions');
                  } catch (err: any) {
                    setErrorMessage(err.message ?? 'Failed to reject');
                  }
                }}
              >
                {rejecting ? 'Rejecting…' : 'Reject'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </MainWrapper>
  );
}

const query = gql<ConfirmAdminCredentialsByTokenGQL>`
  query ConfirmAdminCredentialsByToken($token: String!) {
    pendingAdminActionByToken(token: $token) {
      id
      kind
      adminEmail
      adminName
      adminRole
      initiatedByEmail
      expiresAt
      confirmedByEmail
      status
    }
  }
`;

const confirmMutation = gql<AdminCredentialsConfirmGQL>`
  mutation AdminCredentialsConfirm($token: String!) {
    adminCredentialsConfirm(token: $token) {
      id
      status
    }
  }
`;

const revokeConfirmMutation = gql<AdminCredentialsRevokeConfirmGQL>`
  mutation AdminCredentialsRevokeConfirm($token: String!) {
    adminCredentialsRevokeConfirm(token: $token) {
      id
      status
    }
  }
`;

const rejectMutation = gql<AdminCredentialsRejectGQL>`
  mutation AdminCredentialsReject($id: ID!, $reason: String) {
    pendingAdminActionReject(id: $id, reason: $reason) {
      id
      status
    }
  }
`;
